// A table of saves (named slots or autosaves): name, season and turn, people, date, and Load
// (and Delete for slots). Used by the title screen and the in-game saves overlay.
import { useState } from 'preact/hooks';
import type { SaveRow } from '../../viewModels';
import { seasonName } from '../format';
import { uiText } from '../text/en';

interface Props {
  rows: SaveRow[];
  caption: string;
  empty: string;
  onLoad: (row: SaveRow) => void;
  onDelete?: (row: SaveRow) => void;
}

function date(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function SaveList({ rows, caption, empty, onLoad, onDelete }: Props) {
  const t = uiText.saves;
  const [confirming, setConfirming] = useState<string | null>(null);
  if (rows.length === 0) return <p class="hint">{empty}</p>;
  return (
    <table class="table saves__table">
      <caption class="visually-hidden">{caption}</caption>
      <thead>
        <tr>
          <th scope="col">{t.saveName}</th>
          <th scope="col">{uiText.header.season}</th>
          <th scope="col">{uiText.header.population}</th>
          <th scope="col">
            <span class="visually-hidden">Saved</span>
          </th>
          <th scope="col">
            <span class="visually-hidden">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.kind}-${r.id}`} class={r.current ? 'is-current' : ''}>
            <th scope="row" class="name">
              {r.kind === 'start' ? t.start : (r.name ?? r.seed)}
              {r.current && <span class="tag"> {t.current}</span>}
              {r.ironman && <span class="tag"> {t.ironmanTag}</span>}
              {r.outcome !== null && <span class="tag"> {r.outcome === 'won' ? t.won : t.lost}</span>}
            </th>
            <td>{t.row(seasonName(r.season), r.year, r.turn)}</td>
            <td class="num">{r.population === null ? '' : r.population}</td>
            <td class="hint">{date(r.savedAt)}</td>
            <td class="saves__actions">
              <button type="button" class="button button--small" onClick={() => onLoad(r)}>
                {t.load}
              </button>
              {onDelete !== undefined && confirming !== r.id && (
                <button type="button" class="button button--small" onClick={() => setConfirming(r.id)}>
                  {t.delete}
                </button>
              )}
              {onDelete !== undefined && confirming === r.id && (
                <span class="saves__confirm" role="group" aria-label={t.confirmDelete(r.name ?? r.id)}>
                  <span class="hint">{t.confirmDelete(r.name ?? r.id)}</span>{' '}
                  <button
                    type="button"
                    class="button button--small"
                    onClick={() => {
                      setConfirming(null);
                      onDelete(r);
                    }}
                  >
                    {t.yesDelete}
                  </button>
                  <button type="button" class="button button--small" onClick={() => setConfirming(null)}>
                    {t.keep}
                  </button>
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
